import { WidgetDataRow } from '../types';

export interface GroupedSeries {
  categories: string[];
  series: { name: string; data: number[] }[];
  /** For each category index, a map of seriesName → the original full row */
  rowLookup: Map<string, Map<string, WidgetDataRow>>;
}

/**
 * Groups flat rows into multiple ECharts series based on a key column.
 *
 * @param rows      — query result rows
 * @param xCol      — column used for the X-axis (categories)
 * @param yCol      — column used for the Y-axis (numeric value)
 * @param keyCol    — column whose unique values define separate series
 * @param labelCol  — optional column for human-readable series names (falls back to keyCol value)
 */
export function groupByKey(
  rows: WidgetDataRow[],
  xCol: string,
  yCol: string,
  keyCol: string,
  labelCol?: string,
): GroupedSeries {
  // Collect unique categories in order of first appearance
  const categorySet = new Set<string>();
  // Map: keyValue → label, preserving insertion order
  const keyLabelMap = new Map<string, string>();
  // Map: keyValue → Map<category, { yValue, row }>
  const grouped = new Map<string, Map<string, { y: number; row: WidgetDataRow }>>();

  for (const row of rows) {
    const cat = String(row[xCol] ?? '');
    const keyVal = String(row[keyCol] ?? '');
    const yVal = Number(row[yCol]) || 0;
    const displayLabel = labelCol ? String(row[labelCol] ?? keyVal) : keyVal;

    categorySet.add(cat);

    if (!keyLabelMap.has(keyVal)) {
      keyLabelMap.set(keyVal, displayLabel);
    }

    if (!grouped.has(keyVal)) {
      grouped.set(keyVal, new Map());
    }
    grouped.get(keyVal)!.set(cat, { y: yVal, row });
  }

  const categories = Array.from(categorySet);

  // Build rowLookup: category → seriesName → row
  const rowLookup = new Map<string, Map<string, WidgetDataRow>>();

  const series = Array.from(keyLabelMap.entries()).map(([keyVal, displayName]) => {
    const catMap = grouped.get(keyVal)!;
    const data = categories.map((cat) => {
      const entry = catMap.get(cat);
      if (entry) {
        // Store row for click lookup
        if (!rowLookup.has(cat)) rowLookup.set(cat, new Map());
        rowLookup.get(cat)!.set(displayName, entry.row);
      }
      return entry?.y ?? 0;
    });
    return { name: displayName, data };
  });

  return { categories, series, rowLookup };
}
